import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, Trash2, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/api";

export function Header() {
  const { items, count, total, setQty, removeItem } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-border/40">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <img src="/logo.jpg" alt="Jehovah Nissi Solar Agency logo" className="w-10 h-10 rounded-md object-cover transition-transform duration-200 group-hover:scale-105" />
          <span className="font-heading font-bold text-base sm:text-lg leading-tight">
            Jehovah Nissi <span className="text-primary">Solar Agency</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <a href="/#shop" data-testid="nav-shop-link" className="hover:text-primary transition-colors duration-200">Shop</a>
          <a href="/#why-solar" data-testid="nav-why-link" className="hover:text-primary transition-colors duration-200">Why Solar</a>
          <a href="/#contact" data-testid="nav-contact-link" className="hover:text-primary transition-colors duration-200">Contact</a>
        </nav>
        <a href="tel:+916374135100" data-testid="nav-phone-link" className="hidden lg:flex items-center gap-2 text-sm font-semibold text-secondary hover:text-primary transition-colors duration-200">
          <Phone className="w-4 h-4" strokeWidth={2} /> +91 63741 35100
        </a>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="cart-open-button" className="relative flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium hover:border-primary hover:text-primary transition-colors duration-200 focus:ring-2 focus:ring-primary">
              <ShoppingCart className="w-4 h-4" strokeWidth={2} />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-primary text-white text-xs flex items-center justify-center font-semibold">
                  {count}
                </span>
              )}
            </button>
          </SheetTrigger>
          <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md flex flex-col">
            <SheetHeader>
              <SheetTitle className="font-heading text-xl">Your Cart</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto py-6 space-y-4">
              {items.length === 0 && (
                <p data-testid="cart-empty-message" className="text-sm text-muted-foreground">Your cart is empty. Add some solar products to get started.</p>
              )}
              {items.map(({ product, qty }) => (
                <div key={product.id} data-testid={`cart-item-${product.id}`} className="flex gap-4 border border-border rounded-md p-3 bg-white">
                  <img src={product.image} alt={product.name} className="w-16 h-16 rounded-md object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{product.name}</p>
                    <p className="text-sm text-primary font-semibold">{inr(product.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button data-testid={`qty-minus-${product.id}`} onClick={() => setQty(product.id, qty - 1)} className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors duration-200">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span data-testid={`qty-value-${product.id}`} className="text-sm w-6 text-center">{qty}</span>
                      <button data-testid={`qty-plus-${product.id}`} onClick={() => setQty(product.id, qty + 1)} className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors duration-200">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button data-testid={`remove-item-${product.id}`} onClick={() => removeItem(product.id)} className="ml-auto text-muted-foreground hover:text-destructive transition-colors duration-200">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex items-center justify-between font-heading font-semibold text-lg">
                <span>Total</span>
                <span data-testid="cart-total">{inr(total)}</span>
              </div>
              <Button
                data-testid="proceed-to-checkout-button"
                disabled={items.length === 0}
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="w-full rounded-full bg-primary hover:bg-primary/90 text-white h-11"
              >
                Proceed to Checkout
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
